import React, { useEffect, useRef, useState } from 'react';

import Carousel from '@itseasy21/react-elastic-carousel';
import FiberManualRecordIcon from '@mui/icons-material/FiberManualRecord';
import Grid from '@mui/material/Unstable_Grid2';
import IconButton from '@mui/material/IconButton';
import KeyboardArrowLeftIcon from '@mui/icons-material/KeyboardArrowLeft';
import KeyboardArrowRightIcon from '@mui/icons-material/KeyboardArrowRight';
import styles from '../../styles/FlipCard.module.scss';
import { useTheme } from '@mui/material/styles';

interface BreakPointArray {
  width: number;
  itemsToShow: number;
}

interface CustomCarouselProps {
  currentItemIndex: number;
  setCurrentItemIndex: (value: number) => void;
  goToIndex?: number; //screen capture component specific props
  breakPoints: BreakPointArray[];
  items: React.ReactNode[];
}
export const CustomCarousel = (props: CustomCarouselProps) => {
  const { currentItemIndex, setCurrentItemIndex } = props;
  const [visibleItemsIndexes, setVisibleItemsIndexes] = useState<number[]>([]);
  const [itemsToShow, setItemsToShow] = useState(0);

  const container = useRef<HTMLDivElement>();
  const carousel = useRef<any>(null);
  const muiTheme = useTheme();

  const items = props.items;
  const numItems = props.items?.length || 0;
  const primaryColor = muiTheme.palette.primary.main;

  useEffect(() => {
    setTimeout(() => {
      if (items?.length) {
        getVisibleItems();
      }
    }, 50);
  }, [items?.length, currentItemIndex]);

  useEffect(() => {
    if (props.goToIndex) {
      goTo(props.goToIndex);
    }
  }, [props.goToIndex]);

  const getVisibleItems = () => {
    const carouselItemsElement =
      container?.current?.getElementsByClassName('rec-carousel-item');
    if (carouselItemsElement) {
      let carouselItemsElementArry = Array.from(carouselItemsElement);
      const visibleItemsIndexes = carouselItemsElementArry.map(
        (element, index) => {
          return element.className.includes(`rec-carousel-item-visible`)
            ? index
            : null;
        }
      );
      const filteredItems = visibleItemsIndexes.filter((item) => item !== null);
      setVisibleItemsIndexes(filteredItems);
    }
  };

  /**
   * go to the current item
   */
  const goTo = (index: number) => {
    carousel?.current['goTo'](index);
  };

  /** Show previous item  */
  const handleShowPreviousItem = () => {
    const newIndex =
      currentItemIndex === 0
        ? numItems - 1
        : visibleItemsIndexes.length > 1
        ? visibleItemsIndexes[0] - 1
        : currentItemIndex - 1;
    setCurrentItemIndex(newIndex);
    goTo(newIndex);
  };

  /** Show next item  */
  const handleShowNextItem = () => {
    const newIndex =
      currentItemIndex === numItems - 1 ? 0 : currentItemIndex + 1;
    setCurrentItemIndex(newIndex);
    goTo(newIndex);
    getVisibleItems();
  };

  /** Handle dot click */
  const handleSelectItem = (index: number) => {
    setCurrentItemIndex(index);
    goTo(index);
    getVisibleItems();
  };

  /** Set the current item when resize the screen. */
  const handleResize = ({ ...props }) => {
    setItemsToShow(props.itemsToShow);
    goTo(currentItemIndex);
    getVisibleItems();
  };

  /** Custom pagination dots */
  const customPagination = () => {
    return (
      <Grid
        container
        justifyContent={'space-around'}
        style={{ minWidth: '60%' }}
      >
        <Grid>
          <IconButton
            size="small"
            onClick={handleShowPreviousItem}
            disabled={visibleItemsIndexes[0] === 0}
          >
            <KeyboardArrowLeftIcon />
          </IconButton>
        </Grid>
        <Grid>
          {items?.map((item, idx) => (
            <IconButton
              key={`item-${idx}`}
              size="small"
              onClick={() => handleSelectItem(idx)}
            >
              <FiberManualRecordIcon
                htmlColor={
                  (
                    itemsToShow > 1
                      ? visibleItemsIndexes.includes(idx)
                      : currentItemIndex === idx
                  )
                    ? primaryColor
                    : 'disabled'
                }
                className={styles.paginationDots}
              />
            </IconButton>
          ))}
        </Grid>
        <Grid>
          <IconButton
            size="small"
            onClick={handleShowNextItem}
            disabled={
              visibleItemsIndexes[visibleItemsIndexes.length - 1] >=
              items?.length - 1
            }
          >
            <KeyboardArrowRightIcon />
          </IconButton>
        </Grid>
      </Grid>
    );
  };

  return (
    <div ref={container}>
      {/* Carousel styles are overridden from 'LibOverride.css' */}
      <Carousel
        ref={carousel}
        pagination={numItems > 1}
        isRTL={false}
        breakPoints={props.breakPoints}
        showArrows={false}
        renderPagination={customPagination}
        onResize={handleResize}
      >
        {items}
      </Carousel>
    </div>
  );
};
